import { Input, Flex as WebFlex } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { PDFOnly } from '../../../summary/components/PDFOnly.tsx';
import { Typography } from '../../../summary/components/Typography.tsx';
import { WebOnly } from '../../../summary/components/WebOnly.tsx';
import { SummarySection } from '../SummarySection.tsx';
import { SummaryTableHorizontal } from '../SummaryTableHorizontal.js';
import { TitleAndExportToggleLockup } from '../TitleAndExportToggleLockup.tsx';
import { BreakdownCategory, Tkv2PairedCostSummaryInterfaceV2 } from '../types.ts';
import { adjustment } from '../utils/formatters/adjustment.ts';
import { calculations } from '../utils/formatters/calculations.ts';
import { PairedCostSettingsBreakdown } from './PairedCostSettingsBreakdown.tsx';
import { isCosted } from './isCosted.ts';

export const AdjustmentBreakdownAdditionalStructure = (props: {
  summary: Tkv2PairedCostSummaryInterfaceV2;
  breakdownCategory: Exclude<BreakdownCategory, 'allImprovements' | 'market'>;
  compSequence: string[];
  id: string;
  title: string;
}) => {
  const { breakdownCategory, summary } = props;
  const commentaryProps = props.summary.exportableCommentaries[breakdownCategory];
  const [commentary, setCommentary] = useState(commentaryProps.commentary ?? '');

  if (!isCosted(summary.results, breakdownCategory)) {
    return null;
  }

  const { title } = props;
  const structureName = title.toLowerCase();

  return (
    <SummarySection>
      <TitleAndExportToggleLockup {...props} commentary={commentaryProps} />
      <Typography variant="body1">
        {`The ${structureName} adjustment is calculated by subtracting the comparable’s ${structureName} value from the subject’s ${structureName} value.`}
      </Typography>
      <PDFOnly>
        <Typography variant="body1">{commentaryProps.commentary ?? ''}</Typography>
      </PDFOnly>
      <SummaryTableHorizontal
        {...props}
        columns={[
          {
            header: 'Square Feet',
            type: 'feature',
            path: 'text',
            adjustmentType: breakdownCategory,
          },
          {
            header: 'Adjustment Calculation',
            type: 'adjustment',
            path: 'calculations',
            valueFormatter: calculations,
            adjustmentType: breakdownCategory,
            includeTooltip: false,
          },
          {
            header: 'Adjustment',
            type: 'adjustment',
            path: 'adjustment',
            valueFormatter: adjustment,
            textAlign: 'right',
            adjustmentType: breakdownCategory,
            includeTooltip: true,
          },
        ]}
      />
      <PairedCostSettingsBreakdown {...props.summary} type={breakdownCategory} />
      <WebOnly>
        <WebFlex marginTop="1rem" width="40rem">
          <Input
            type="multiline"
            onChange={setCommentary}
            value={commentary}
            onClear={() => setCommentary('')}
            onBlur={() => commentaryProps.updateCommentary(commentary)}
            resize="both"
            label="Commentary"
            size="sm"
          />
        </WebFlex>
      </WebOnly>
    </SummarySection>
  );
};
